import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import {
  CheckCircle2, XCircle, Clock, Eye, Filter, RefreshCw, ClipboardCheck,
  TrendingUp, AlertTriangle, Building2, Calendar,
} from 'lucide-react';
import {
  useQualityGateQueue,
  useQualityGateHistory,
  useQualityGateMutation,
  CATEGORIA_DEFEITO_OPTIONS,
  QualityGateItem,
  CategoriaDefeito,
} from '@/hooks/useQualityGate';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useNavigate } from 'react-router-dom';

const formatDate = (d: string | null) => {
  if (!d) return '—';
  try { return format(parseISO(d.includes('T') ? d : d + 'T00:00:00'), 'dd/MM/yyyy', { locale: ptBR }); }
  catch { return d; }
};

const QualityStatusBadge = ({ status }: { status: string | null }) => {
  if (!status || status === 'pendente') return (
    <Badge variant="outline" className="text-xs gap-1">
      <Clock className="h-3 w-3" /> Aguardando
    </Badge>
  );
  if (status === 'aprovada') return (
    <Badge variant="default" className="text-xs gap-1 bg-green-600">
      <CheckCircle2 className="h-3 w-3" /> Aprovada
    </Badge>
  );
  return (
    <Badge variant="destructive" className="text-xs gap-1">
      <XCircle className="h-3 w-3" /> Rejeitada
    </Badge>
  );
};

export default function QualityGatePage() {
  const navigate = useNavigate();
  const { data: queue = [], isLoading, refetch, isFetching } = useQualityGateQueue();
  const { data: history = [] } = useQualityGateHistory();
  const mutation = useQualityGateMutation();

  // Filters
  const [contratoFilter, setContratoFilter] = useState('all');
  const [qualFilter, setQualFilter] = useState('pendente'); // pendente | aprovada | rejeitada | all
  const [search, setSearch] = useState('');

  // Dialog state
  const [dialogItem, setDialogItem] = useState<QualityGateItem | null>(null);
  const [decision, setDecision] = useState<'aprovada' | 'rejeitada'>('aprovada');
  const [obs, setObs] = useState('');
  const [categoria, setCategoria] = useState<CategoriaDefeito | ''>('');

  // Statistics from history (90 days)
  const stats = useMemo(() => {
    const total = history.length;
    const aprovadas = history.filter((h: any) => h.qualidade_status === 'aprovada').length;
    const rejeitadas = history.filter((h: any) => h.qualidade_status === 'rejeitada').length;
    return {
      total,
      aprovadas,
      rejeitadas,
      taxaAprovacao: total > 0 ? Math.round((aprovadas / total) * 100) : null,
    };
  }, [history]);

  const pendingCount = queue.filter(i => !i.qualidade_status || i.qualidade_status === 'pendente').length;
  const approvedCount = queue.filter(i => i.qualidade_status === 'aprovada').length;
  const rejectedCount = queue.filter(i => i.qualidade_status === 'rejeitada').length;

  // Unique contratos for filter
  const contratos = useMemo(() => {
    const map = new Map<string, string>();
    queue.forEach(i => map.set(i.contrato_id, `${i.contrato_numero} — ${i.contrato_nome}`));
    return Array.from(map.entries()).map(([id, label]) => ({ id, label }));
  }, [queue]);

  // Filtered list
  const filtered = useMemo(() => {
    return queue.filter(item => {
      if (contratoFilter !== 'all' && item.contrato_id !== contratoFilter) return false;
      const qs = item.qualidade_status || 'pendente';
      if (qualFilter !== 'all' && qs !== qualFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        const matches =
          item.item_codigo?.toLowerCase().includes(q) ||
          item.item_descricao?.toLowerCase().includes(q) ||
          item.contrato_nome?.toLowerCase().includes(q) ||
          item.contrato_numero?.toLowerCase().includes(q) ||
          item.localizacao_descricao?.toLowerCase().includes(q);
        if (!matches) return false;
      }
      return true;
    });
  }, [queue, contratoFilter, qualFilter, search]);

  const openDialog = (item: QualityGateItem, dec: 'aprovada' | 'rejeitada') => {
    setDialogItem(item);
    setDecision(dec);
    setObs('');
    setCategoria('');
  };

  const handleConfirm = () => {
    if (!dialogItem) return;
    mutation.mutate({
      execucaoId: dialogItem.id,
      contratoId: dialogItem.contrato_id,
      decision,
      observacoes: obs,
      categoria: decision === 'rejeitada' ? (categoria as CategoriaDefeito) : undefined,
    }, {
      onSuccess: () => {
        setDialogItem(null);
        setObs('');
        setCategoria('');
      },
    });
  };

  const canConfirm = decision === 'aprovada'
    ? true
    : obs.trim().length >= 10 && !!categoria;

  return (
    <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Portal da Qualidade
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Fila centralizada de execuções aguardando inspeção de qualidade
            </p>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            <RefreshCw className={`h-4 w-4 mr-1.5 ${isFetching ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Card className="border-orange-200 dark:border-orange-800">
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <Clock className="h-3.5 w-3.5 text-orange-500" /> Aguardando Inspeção
              </div>
              <p className="text-2xl font-bold text-orange-500" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {pendingCount}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">em fila aberta</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <CheckCircle2 className="h-3.5 w-3.5 text-green-600" /> Aprovadas (fila)
              </div>
              <p className="text-2xl font-bold text-green-600" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {approvedCount}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">aguardando planejamento</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <XCircle className="h-3.5 w-3.5 text-destructive" /> Rejeitadas (fila)
              </div>
              <p className="text-2xl font-bold text-destructive" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {rejectedCount}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">retrabalho necessário</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-4 pb-3">
              <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1">
                <TrendingUp className="h-3.5 w-3.5 text-primary" /> Taxa Aprovação (90d)
              </div>
              <p className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {stats.taxaAprovacao !== null ? `${stats.taxaAprovacao}%` : '—'}
              </p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {stats.aprovadas} aprovadas / {stats.total} avaliadas
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4 pb-3">
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Filter className="h-4 w-4" /> Filtros
              </div>
              <div className="flex-1 min-w-[200px]">
                <Label className="text-xs mb-1 block">Busca</Label>
                <Input
                  placeholder="Item, descrição, contrato..."
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  className="h-8 text-sm"
                />
              </div>
              <div className="min-w-[180px]">
                <Label className="text-xs mb-1 block">Contrato</Label>
                <Select value={contratoFilter} onValueChange={setContratoFilter}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os contratos</SelectItem>
                    {contratos.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="min-w-[160px]">
                <Label className="text-xs mb-1 block">Status Qualidade</Label>
                <Select value={qualFilter} onValueChange={setQualFilter}>
                  <SelectTrigger className="h-8 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="pendente">Aguardando</SelectItem>
                    <SelectItem value="aprovada">Aprovadas</SelectItem>
                    <SelectItem value="rejeitada">Rejeitadas</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-xs text-muted-foreground self-end pb-0.5">
                {filtered.length} resultado{filtered.length !== 1 ? 's' : ''}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Queue Table */}
        <Card>
          <CardHeader className="py-3 flex flex-row items-center gap-2">
            <ClipboardCheck className="h-4 w-4 text-primary" />
            <CardTitle className="text-sm font-medium">
              Fila de Inspeção de Qualidade
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {isLoading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-12 w-full" />)}
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <CheckCircle2 className="h-12 w-12 mx-auto mb-3 text-green-500/40" />
                <p className="text-base font-medium">Fila limpa</p>
                <p className="text-sm mt-1">
                  {queue.length === 0
                    ? 'Nenhuma execução aguardando inspeção de qualidade.'
                    : 'Nenhum resultado para os filtros aplicados.'}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Data</TableHead>
                    <TableHead>Contrato</TableHead>
                    <TableHead>Item</TableHead>
                    <TableHead>Tipo</TableHead>
                    <TableHead className="text-right">Qtd</TableHead>
                    <TableHead>Local</TableHead>
                    <TableHead>Qualidade</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map(item => {
                    const isPending = !item.qualidade_status || item.qualidade_status === 'pendente';
                    return (
                      <TableRow
                        key={item.id}
                        className={isPending ? 'bg-orange-50/30 dark:bg-orange-950/10' : ''}
                      >
                        <TableCell className="text-sm font-medium whitespace-nowrap">
                          {formatDate(item.data_execucao)}
                        </TableCell>
                        <TableCell>
                          <div className="text-xs font-medium">{item.contrato_numero}</div>
                          <div className="text-xs text-muted-foreground max-w-[150px] truncate">
                            {item.contrato_nome}
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[180px]">
                          <div className="text-xs font-medium">{item.item_codigo || '—'}</div>
                          <div className="text-xs text-muted-foreground truncate">
                            {item.item_descricao || '—'}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={item.tipo === 'retrabalho' ? 'destructive' : 'outline'}
                            className="text-xs"
                          >
                            {item.tipo === 'retrabalho' ? '↺ Retrab.' : 'Execução'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-right text-sm font-semibold">
                          {item.quantidade_executada} {item.unidade}
                        </TableCell>
                        <TableCell className="max-w-[120px]">
                          <span className="text-xs text-muted-foreground truncate block">
                            {item.localizacao_descricao || '—'}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="flex flex-col gap-1">
                            <QualityStatusBadge status={item.qualidade_status} />
                            {item.qualidade_observacoes && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <span className="text-xs text-muted-foreground italic cursor-help truncate max-w-[120px] block">
                                    {item.qualidade_observacoes}
                                  </span>
                                </TooltipTrigger>
                                <TooltipContent className="max-w-xs">
                                  {item.qualidade_observacoes}
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            {/* Navigate to contract */}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => navigate(`/empreendimentos/contratos/${item.contrato_id}/execucao`)}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>Ver no contrato</TooltipContent>
                            </Tooltip>

                            {isPending && item.status === 'aguardando_qualidade' && (
                              <>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                      onClick={() => openDialog(item, 'aprovada')}
                                    >
                                      <CheckCircle2 className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Aprovar qualidade</TooltipContent>
                                </Tooltip>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="text-destructive hover:text-destructive hover:bg-destructive/10"
                                      onClick={() => openDialog(item, 'rejeitada')}
                                    >
                                      <XCircle className="h-4 w-4" />
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent>Reprovar qualidade</TooltipContent>
                                </Tooltip>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>


      {/* Decision Dialog */}
      <Dialog open={!!dialogItem} onOpenChange={(open) => !open && setDialogItem(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className={`flex items-center gap-2 ${decision === 'aprovada' ? 'text-green-700' : 'text-destructive'}`}>
              {decision === 'aprovada'
                ? <><CheckCircle2 className="h-5 w-5" /> Aprovar Qualidade</>
                : <><XCircle className="h-5 w-5" /> Reprovar Qualidade</>
              }
            </DialogTitle>
            <DialogDescription>
              {dialogItem && (
                <span className="text-xs">
                  Contrato <strong>{dialogItem.contrato_numero}</strong>
                  {' · '}
                  Execução de <strong>{formatDate(dialogItem?.data_execucao)}</strong>
                  {dialogItem.item_codigo && <> · Item <strong>{dialogItem.item_codigo}</strong></>}
                </span>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {/* Category — required only on rejection */}
            {decision === 'rejeitada' && (
              <div>
                <Label className="flex items-center gap-1.5 mb-1.5">
                  <AlertTriangle className="h-3.5 w-3.5 text-destructive" />
                  Categoria do Defeito *
                </Label>
                <div className="grid grid-cols-2 gap-2">
                  {CATEGORIA_DEFEITO_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setCategoria(opt.value)}
                      className={`p-2.5 text-left rounded-lg border text-xs transition-all ${
                        categoria === opt.value
                          ? 'border-destructive bg-destructive/10 font-semibold'
                          : 'border-border hover:border-destructive/50 hover:bg-destructive/5'
                      }`}
                    >
                      <div className="font-medium">{opt.label}</div>
                      <div className="text-muted-foreground mt-0.5">{opt.description}</div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Observations */}
            <div>
              <Label className="mb-1.5 block">
                {decision === 'rejeitada'
                  ? 'Observações da Rejeição *'
                  : 'Observações (opcional)'}
              </Label>
              <Textarea
                value={obs}
                onChange={e => setObs(e.target.value)}
                placeholder={
                  decision === 'rejeitada'
                    ? 'Descreva o defeito encontrado, localização e condição observada...'
                    : 'Observações sobre a inspeção (opcional)...'
                }
                rows={3}
                className={decision === 'rejeitada' && obs.trim().length < 10 && obs.length > 0 ? 'border-destructive' : ''}
              />
              {decision === 'rejeitada' && (
                <p className="text-xs text-muted-foreground mt-1">
                  Mínimo 10 caracteres. {obs.trim().length}/10
                </p>
              )}
            </div>

            {/* Validation summary */}
            {decision === 'rejeitada' && (
              <div className={`p-3 rounded-lg text-xs space-y-1 ${canConfirm ? 'bg-green-50 dark:bg-green-950/20 border border-green-200' : 'bg-muted border border-border'}`}>
                <div className={`flex items-center gap-1.5 ${categoria ? 'text-green-700' : 'text-muted-foreground'}`}>
                  {categoria ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                  Categoria: {categoria ? CATEGORIA_DEFEITO_OPTIONS.find(o => o.value === categoria)?.label : 'Não selecionada'}
                </div>
                <div className={`flex items-center gap-1.5 ${obs.trim().length >= 10 ? 'text-green-700' : 'text-muted-foreground'}`}>
                  {obs.trim().length >= 10 ? <CheckCircle2 className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
                  Observação: {obs.trim().length >= 10 ? 'Preenchida' : `${obs.trim().length}/10 caracteres`}
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogItem(null)}>Cancelar</Button>
            <Button
              variant={decision === 'rejeitada' ? 'destructive' : 'default'}
              disabled={!canConfirm || mutation.isPending}
              onClick={handleConfirm}
              className={decision === 'aprovada' ? 'bg-green-600 hover:bg-green-700' : ''}
            >
              {mutation.isPending
                ? 'Registrando...'
                : decision === 'aprovada'
                  ? '✓ Confirmar Aprovação'
                  : '✗ Confirmar Rejeição'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
